import { getOnboardingSteps } from "creso-core/utils";
import { Client } from "creso-core/types";
import { useClient, useInvestorPrimaryBankAccount } from "creso-core/hooks";
import { useCallback, useMemo, type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";
import { Button } from "web-design-system";
import { toast } from "sonner";
import { handleError } from "@/utils/error";
import {
  clientStatusContentClassName,
  ClientStatusLayout,
  ClientStatusMemberDisplay,
} from "./client-status-layout";
import { useKycBanners } from "./use-kyc-banners";

export function ClientStatusSkeleton({
  memberDisplay,
}: {
  memberDisplay?: ClientStatusMemberDisplay;
}) {
  return (
    <ClientStatusLayout memberDisplay={memberDisplay}>
      <div
        className={clientStatusContentClassName(
          memberDisplay,
          "justify-between rounded-lg bg-muted h-15"
        )}
      >
        <div className="rounded-sm h-4 w-100 bg-secondary animate-pulse"></div>
        <div className="w-40 h-10 rounded-sm bg-secondary animate-pulse"></div>
      </div>
    </ClientStatusLayout>
  );
}

// Cards as data, not a self-framing component — the dispatcher collects
// cards from every branch and is the only place that knows the real count.
export function useSetupPendingStatusCards(
  client: Client,
  memberDisplay: ClientStatusMemberDisplay | undefined,
  enabled: boolean
): ReactNode[] {
  const { data: bankDetailsFromApi, isPending: isBankDetailsPending } =
    useInvestorPrimaryBankAccount(
      enabled ? client?.investment_account || "" : ""
    );
  const bankDetails = bankDetailsFromApi;
  const isPending = enabled && isBankDetailsPending;

  const { refetch: refetchClient, isRefetching: isRefetchingClient } =
    useClient(client.id, false);

  const router = useRouter();

  const kycBanner = useKycBanners(client);

  const isOnboardingComplete =
    bankDetails?.verification_status === "verified" &&
    client?.kyc_status === "S";

  const checkAccountUpdateStatus = useCallback(async () => {
    refetchClient()
      .then(({ data: client }) => {
        if (client?.status === "ACTIVE") {
          toast.success("Account is now activated, and ready for transactions");
        } else {
          toast.info(
            "Account is still under review and will be activated shortly"
          );
        }
      })
      .catch((e) => handleError(e, "Failed to check status"));
  }, [refetchClient]);

  const setupPendingStates = useMemo(() => {
    if (!enabled) return [];
    const steps = [];

    if (kycBanner) {
      steps.push({ id: "kyc", ...kycBanner });
      return steps;
    }

    if (bankDetails?.verification_status === "failed") {
      steps.push({
        id: "bank",
        description: `Bank details verification failed - ${
          bankDetails?.verification_remark
        }`,
        actions: (
          <Button
            onClick={() => {
              router.navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  modal: "onboarding",
                  client_id: client.id,
                  step:
                    getOnboardingSteps(client).findIndex(
                      (f) => f.id === "bank-details"
                    ) + 1,
                }),
              });
            }}
          >
            Update bank details
          </Button>
        ),
      });
    }
    steps.push({
      id: "finish",
      actions: (
        <>
          {isOnboardingComplete ? (
            <Button
              disabled={isRefetchingClient}
              onClick={checkAccountUpdateStatus}
              variant="outline"
            >
              Check Status
            </Button>
          ) : null}
          <Button
            onClick={() => {
              router.navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  modal: "onboarding",
                  client_id: client.id,
                }),
              });
            }}
          >
            Finish setup
          </Button>
        </>
      ),
      description:
        "Complete the missing details to create the investment account",
    });

    return steps;
  }, [
    enabled,
    kycBanner,
    router,
    client,
    bankDetails?.verification_status,
    bankDetails?.verification_remark,
    checkAccountUpdateStatus,
    isRefetchingClient,
    isOnboardingComplete,
  ]);

  if (!enabled) return [];

  if (isPending) {
    return [
      <ClientStatusSkeleton
        key={`${client.id}-skeleton`}
        memberDisplay={memberDisplay}
      />,
    ];
  }

  return setupPendingStates.map((step) => (
    <ClientStatusLayout
      key={`${client.id}-${step.id}`}
      memberDisplay={memberDisplay}
    >
      <div
        className={clientStatusContentClassName(
          memberDisplay,
          "justify-between gap-2"
        )}
      >
        <span className="text-sm">{step.description}</span>
        <div className="ml-auto flex shrink-0 flex-row gap-2">
          {step.actions}
        </div>
      </div>
    </ClientStatusLayout>
  ));
}
